import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Quote } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Stars from "@/components/Stars";

export default function Testimonials() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, average: 0, items: [] });
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/testimonials", { params: { limit: 6 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, average: 0, items: [] }));
  };
  useEffect(() => { load(); }, []);

  const openForm = () => {
    if (!user) {
      toast.info("Please log in to leave a review.");
      nav("/login?next=/");
      return;
    }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return toast.error("Please share a short note.");
    setBusy(true);
    try {
      await api.post("/testimonials", { rating, comment: comment.trim(), city: city.trim() || null });
      toast.success("Thanks for sharing!");
      setShowForm(false); setComment(""); setCity(""); setRating(5);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="testimonials-section" className="bg-[#FAF6EF] border-y border-stone-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow">In their own words</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">What our customers say</h2>
            {data.count > 0 && (
              <div className="mt-3 flex items-center gap-2" data-testid="testimonials-average">
                <Stars value={data.average} size={16} />
                <span className="text-sm text-stone-600">{data.average.toFixed(1)} average · {data.count} customer{data.count === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>
          <button
            data-testid="testimonials-write-btn"
            onClick={openForm}
            className="btn-secondary"
          >
            Write a Review
          </button>
        </div>

        {/* List */}
        {data.items.length === 0 ? (
          <p className="mt-10 text-stone-500 italic">Be the first to share your experience.</p>
        ) : (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {data.items.map((t) => (
              <blockquote key={t.id} data-testid={`testimonial-${t.id}`} className="bg-white p-8 border border-stone-200 relative">
                <Quote size={22} className="text-[#E7D3B3] mb-3" strokeWidth={1.5}/>
                <Stars value={t.rating} size={12} />
                <p className="mt-4 font-serif text-lg leading-relaxed text-stone-800">{t.comment}</p>
                <footer className="mt-5 text-xs tracking-widest uppercase text-stone-500">
                  {t.user_name}{t.city ? ` · ${t.city}` : ""}
                </footer>
              </blockquote>
            ))}
          </div>
        )}
      </div>

      {/* Modal form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            data-testid="testimonial-form"
            className="bg-white w-full max-w-md p-8 border border-stone-200"
          >
            <p className="eyebrow">Share your experience</p>
            <h3 className="font-serif text-2xl mt-2">Tell us how we did.</h3>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-stone-600">Your rating</span>
              <Stars value={rating} size={22} onChange={setRating} testid="testimonial-rating-picker" />
            </div>
            <textarea
              data-testid="testimonial-comment-input"
              placeholder="How was your Vardhman experience?"
              rows={4}
              required
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-4 w-full"
              maxLength={500}
            />
            <input
              data-testid="testimonial-city-input"
              type="text"
              placeholder="Your city (optional)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="mt-3 w-full"
              maxLength={50}
            />
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="text-sm tracking-widest uppercase text-stone-500">Cancel</button>
              <button data-testid="testimonial-submit-btn" type="submit" disabled={busy} className="btn-primary disabled:opacity-60">
                {busy ? "Posting…" : "Post"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
